// ═══════════════════════════════════════════════════════════════════
// Booking Engine Analytics Dashboard
// Data: real API endpoints (BookingEngineAnalyticsController)
// Fallback: graceful N/A display when daily_stats not yet populated
// ═══════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Table, Tag, Button, Progress, Tabs, Spin, Alert } from "antd";
import { Download, TrendingUp, TrendingDown, RefreshCw } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";

import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  PieChart, Pie, Cell, BarChart, Bar, ComposedChart,
} from "recharts";

// ─── Config ───────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || "https://api.netshineoffice.com/api";
// NOTE: companyId is read inside the component (not at module level)
// so it's always fresh from localStorage when the component mounts.

// ─── Utils ────────────────────────────────────────────────────────
const moneyINR = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const pct = (v, d = 1) => v == null ? "N/A" : `${Number(v || 0).toFixed(d)}%`;
const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("en-IN");

// ─── Color Palette ────────────────────────────────────────────────
const COLORS = {
  primary:"#1677ff", success:"#16a34a", danger:"#dc2626",
  purple:"#7c3aed",  amber:"#f59e0b",   cyan:"#06b6d4",
  slate:"#64748b",
};
const PIE_COLORS = ["#1677ff","#7c3aed","#16a34a","#f59e0b","#ef4444","#06b6d4"];

// ─── API calls ────────────────────────────────────────────────────
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// Backend reads companyId from req.companyid set by verifyApiToken middleware.
// No need to pass it as a query param — just send dates.
const fetchOverview = (startDate, endDate) =>
  axios.get(`${API_URL}/booking-engine-settings/analytics/overview`, {
    params: { startDate, endDate },
    headers: authHeaders(),
  }).then((r) => r.data);

const fetchCampaigns = (startDate, endDate) =>
  axios.get(`${API_URL}/booking-engine-settings/analytics/campaigns`, {
    params: { startDate, endDate },
    headers: authHeaders(),
  }).then((r) => r.data);

const fetchRooms = (startDate, endDate) =>
  axios.get(`${API_URL}/booking-engine-settings/analytics/rooms`, {
    params: { startDate, endDate },
    headers: authHeaders(),
  }).then((r) => r.data);

// ─── KPI Card ─────────────────────────────────────────────────────
function KpiCard({ label, value, subLabel, subValue, color, trend }) {
  return (
    <Card className="ns-metric-card" bordered={false}>
      <div className="ns-metric-top">
        <div className="ns-metric-dot" style={{ background: color }} />
        <div className="ns-metric-label">{label}</div>
        {trend !== undefined && trend !== null && (
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:3, fontSize:11, fontWeight:700,
            color: trend >= 0 ? COLORS.success : COLORS.danger }}>
            {trend >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="ns-metric-value" style={{ color }}>{value}</div>
      {subLabel && (
        <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginTop:4 }}>
          {subLabel}: <span style={{ color:"#475569" }}>{subValue}</span>
        </div>
      )}
    </Card>
  );
}

// ─── Mini Pie ─────────────────────────────────────────────────────
function MiniPieCard({ title, data, extra }) {
  const total = data.reduce((a, d) => a + d.value, 0);
  if (total === 0) return (
    <Card className="ns-card" title={title} extra={extra}>
      <div style={{ height:220, display:"flex", alignItems:"center", justifyContent:"center", color:"#94a3b8" }}>
        No data for this period
      </div>
    </Card>
  );
  return (
    <Card className="ns-card" title={title} extra={extra}>
      <div style={{ height:220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2}>
              {data.map((entry, idx) => (
                <Cell key={idx} fill={entry.color || PIE_COLORS[idx % PIE_COLORS.length]} />
              ))}
            </Pie>
            <RTooltip formatter={(v, name) => [`${v} (${total > 0 ? ((v/total)*100).toFixed(1):0}%)`, name]}/>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:"6px 14px", justifyContent:"center", marginTop:4 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, fontWeight:600, color:"#475569" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background: d.color || PIE_COLORS[i%PIE_COLORS.length] }}/>
            {d.name} ({total > 0 ? ((d.value/total)*100).toFixed(0) : 0}%)
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── N/A notice for untracked metrics ────────────────────────────
const NaNotice = () => (
  <Alert
    type="info" showIcon
    message="Session tracking not yet active"
    description="Funnel metrics (sessions, searches, room views) require the event tracking pipeline. Booking-level metrics are real data."
    style={{ marginBottom:16, borderRadius:10 }}
  />
);

// ═══════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════

export default function BeAnalyticsDashboard() {
  const businessMoment = useMemo(() => moment().startOf("day"), []);

  // companyId not needed for API calls (verifyApiToken middleware sets req.companyid).
  // Kept here only for display/debug logging.
  const companyId = useMemo(() =>
    Number(localStorage.getItem("company_id")) ||
    Number(localStorage.getItem("companyid"))  ||
    Number(localStorage.getItem("companyId"))  || null
  , []);

  const [dateRange, setDateRange] = useState({
    startDate: businessMoment.clone().startOf("month"),
    endDate:   businessMoment.clone(),
  });

  const [activeTab,      setActiveTab]      = useState("overview");
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [overview,       setOverview]       = useState(null);
  const [campaigns,      setCampaigns]      = useState([]);
  const [roomTypes,      setRoomTypes]      = useState([]);
  const [filterVisible,  setFilterVisible]  = useState(false);
  const [filters,        setFilters]        = useState({ channel:null, device:null });
  const [searchQuery,    setSearchQuery]    = useState("");

  const drpRanges = useMemo(() => {
    const t = businessMoment.clone();
    return {
      "Today":       [t.clone(), t.clone()],
      "Yesterday":   [t.clone().subtract(1,"day"), t.clone().subtract(1,"day")],
      "Last 7 Days": [t.clone().subtract(6,"days"), t.clone()],
      "This Month":  [t.clone().startOf("month"), t.clone()],
      "Last Month":  [t.clone().subtract(1,"month").startOf("month"), t.clone().subtract(1,"month").endOf("month")],
      "Last 30 Days":[t.clone().subtract(29,"days"), t.clone()],
      "Last 90 Days":[t.clone().subtract(89,"days"), t.clone()],
    };
  }, [businessMoment]);

  // Fetch all three endpoints in parallel
  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    const s = dateRange.startDate.format("YYYY-MM-DD");
    const e = dateRange.endDate.format("YYYY-MM-DD");
    console.log(`[Analytics] Fetching data — companyId: ${companyId}, ${s} → ${e}`);
    try {
      const [ov, camp, rooms] = await Promise.all([
        fetchOverview(s, e),
        fetchCampaigns(s, e),
        fetchRooms(s, e),
      ]);
      if (ov.success)   setOverview(ov);
      if (camp.success) setCampaigns(camp.campaigns || []);
      if (rooms.success)setRoomTypes(rooms.roomTypes || []);
    } catch (err) {
      console.error("[Analytics] API error:", err?.response?.data || err.message || err);
      setError(err?.response?.data?.message || err.message || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  const T  = overview?.totals  || {};
  const K  = overview?.kpis    || {};
  const series = overview?.series || [];
  const hasDailyStats = overview?.meta?.hasDailyStats;

  // ── Campaign table columns ─────────────────────────────────────
  const campaignColumns = [
    { title:"Source / Campaign", key:"source", render:(_,r) => (
      <div>
        <div style={{ fontWeight:700 }}>{r.source}</div>
        <div style={{ fontSize:11, color:"#94a3b8" }}>{r.medium} / {r.campaign}</div>
      </div>
    )},
    { title:"Sessions",    dataIndex:"sessions",  align:"right",
      render:v => v == null ? <Tag color="default">N/A</Tag> : fmt(v),
      sorter:(a,b) => (a.sessions||0)-(b.sessions||0) },
    { title:"Bookings",    dataIndex:"bookings",  align:"right", sorter:(a,b)=>a.bookings-b.bookings },
    { title:"Revenue (₹)", dataIndex:"revenue",   align:"right",
      render:v=>`₹ ${moneyINR(v)}`, sorter:(a,b)=>a.revenue-b.revenue },
    { title:"Conv %",      dataIndex:"conv",      align:"right",
      render:(v,r)=>(
        <Tag color={r.source==="ai_bot"?"purple":"blue"}>
          {v == null ? "N/A" : pct(v, 2)}
        </Tag>
      ), sorter:(a,b)=>(a.conv||0)-(b.conv||0) },
  ];

  // ── Room table columns ─────────────────────────────────────────
  const roomColumns = [
    { title:"Room Type",   dataIndex:"name",      key:"name",
      render:v=><span style={{ fontWeight:700 }}>{v}</span> },
    { title:"Views",       dataIndex:"views",     align:"right",
      render:v => v == null ? <Tag color="default">N/A</Tag> : fmt(v),
      sorter:(a,b)=>(a.views||0)-(b.views||0) },
    { title:"Bookings",    dataIndex:"bookings",  align:"right", sorter:(a,b)=>a.bookings-b.bookings },
    { title:"Revenue (₹)", dataIndex:"revenue",   align:"right",
      render:v=>`₹ ${moneyINR(v)}`, sorter:(a,b)=>a.revenue-b.revenue },
    { title:"Avg Rate (₹)",dataIndex:"avgRate",   align:"right",
      render:v=>`₹ ${moneyINR(v)}` },
    { title:"Nights Sold", dataIndex:"nightsSold",align:"right", sorter:(a,b)=>a.nightsSold-b.nightsSold },
    { title:"Conv %",      dataIndex:"convRate",  align:"right",
      render:v => v == null ? <Tag color="default">N/A</Tag> : <Tag color="blue">{v}%</Tag> },
  ];

  // Tab labels only — content rendered conditionally below
  const tabItems = useMemo(() => [
    { key:"overview",  label:<><i className="bi bi-bar-chart-line me-2"/>Overview</>  },
    { key:"bot",       label:<><i className="bi bi-robot me-2"/>AI Bot</>             },
    { key:"campaigns", label:<><i className="bi bi-megaphone me-2"/>Campaigns</>      },
    { key:"rooms",     label:<><i className="bi bi-door-open me-2"/>Room Types</>     },
  ], []);

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="guest-database-container">

          {/* ── Header ── */}
          <div className="page-header page-top-icons mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div className="ns-icon-circle"><i className="fas fa-chart-line"/></div>
                <div>
                  <div style={{ fontSize:18, fontWeight:900, lineHeight:1.1 }}>Analytics Dashboard</div>
                  <div style={{ color:"#6b7280", fontSize:12 }}>Booking Engine · AI Bot · Revenue Intelligence</div>
                </div>
              </div>

              <div className="ns-top-control">
                <div className="ns-top-label">Date Range</div>
                <DateRangePicker
                  initialSettings={{ startDate:dateRange.startDate, endDate:dateRange.endDate, ranges:drpRanges, autoUpdateInput:true, alwaysShowCalendars:true, locale:{format:"DD MMM YYYY"} }}
                  onApply={(_, picker) => setDateRange({ startDate:picker.startDate.clone().startOf("day"), endDate:picker.endDate.clone().endOf("day") })}
                >
                  <input className="form-control" readOnly
                    style={{ width:260, height:40, borderRadius:10, border:"1px solid #d9d9d9", paddingLeft:12, fontWeight:700 }}
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                  />
                </DateRangePicker>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button className="ns-btn-ghost" onClick={loadData} icon={<RefreshCw size={14}/>}>
                  Refresh
                </Button>
                {/* Download — export current view */}
                <Button className="ns-btn-ghost" icon={<Download size={14}/>}>Export</Button>
              </div>
            </div>
          </div>

          {error && (
            <Alert type="error" showIcon closable message="Failed to load analytics" description={error}
              style={{ marginBottom:16, borderRadius:10 }} onClose={() => setError(null)}/>
          )}

          {/* Loading spinner overlaid, content renders underneath */}
          {loading && (
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 0", color:"#6b7280", fontSize:13, fontWeight:600 }}>
              <Spin size="small"/> Loading analytics data…
            </div>
          )}
          <Spin spinning={loading && !overview} tip="Loading analytics…">

            {/* ── TABS ── */}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              className="be-analytics-tabs"
              style={{ marginBottom:20 }}
            />

            {/* ══════════════ OVERVIEW ══════════════ */}
            {activeTab === "overview" && overview && (
              <>
                {!hasDailyStats && <NaNotice/>}

                {/* KPI Row 1 — Revenue */}
                <Row gutter={[14,14]} className="mb-4">
                  <Col xs={12} md={6}><KpiCard label="Total Revenue"      value={`₹ ${moneyINR(T.revenue)}`}   color={COLORS.success} subLabel="ADR" subValue={`₹ ${moneyINR(K.adr)}`}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Confirmed Bookings"  value={fmt(T.confirmed)}             color={COLORS.primary} subLabel="Cancelled" subValue={fmt(T.cancellations)}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Cancel Rate"         value={pct(K.cancelRate)}            color={COLORS.danger}  subLabel="Refunded" subValue={`₹ ${moneyINR(T.totalRefunded)}`}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Avg Nights"          value={K.avgNights ?? "—"}           color={COLORS.purple}  subLabel="Avg Lead Days" subValue={K.avgLeadDays ?? "—"}/></Col>
                </Row>

                {/* KPI Row 2 — Funnel */}
                <Row gutter={[14,14]} className="mb-4">
                  <Col xs={12} md={6}><KpiCard label="Sessions"     value={T.sessions    == null ? "N/A" : fmt(T.sessions)}     color={COLORS.slate}   subLabel="Need event tracking" subValue=""/></Col>
                  <Col xs={12} md={6}><KpiCard label="Searches"     value={T.searches    == null ? "N/A" : fmt(T.searches)}     color={COLORS.primary} subLabel="" subValue=""/></Col>
                  <Col xs={12} md={6}><KpiCard label="Conversion"   value={K.conversion  == null ? "N/A" : pct(K.conversion)}   color={COLORS.success} subLabel="Commission Saved" subValue={`₹ ${moneyINR(K.commissionSaved)}`}/></Col>
                  <Col xs={12} md={6}><KpiCard label="AI Bot Share" value={pct(K.aiShare)}                                       color={COLORS.purple}  subLabel="Bot Bookings" subValue={fmt(T.botBookings)}/></Col>
                </Row>

                {/* Revenue trend + Source pie */}
                <Row gutter={[14,14]} className="mb-4">
                  <Col xs={24} lg={16}>
                    <Card className="ns-card" title="Revenue & Bookings Trend">
                      <div style={{ height:320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
                            <XAxis dataKey="date" fontSize={11}/>
                            <YAxis yAxisId="left" fontSize={11} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`}/>
                            <YAxis yAxisId="right" orientation="right" fontSize={11}/>
                            <RTooltip formatter={(v,n) => n==="Revenue" ? [`₹ ${moneyINR(v)}`, n] : [v,n]}/>
                            <Legend/>
                            <Area yAxisId="left"  type="monotone" dataKey="revenue"   name="Revenue"   fill={COLORS.success} fillOpacity={0.15} stroke={COLORS.success} strokeWidth={2} dot={false}/>
                            <Bar  yAxisId="right" dataKey="confirmed" name="Confirmed" fill={COLORS.primary} radius={[3,3,0,0]} opacity={0.7}/>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} lg={8}>
                    <MiniPieCard title="Bookings by Source" data={overview.sourcePie || []} extra={<Tag color="blue">All Channels</Tag>}/>
                  </Col>
                </Row>

                {/* Funnel + Device + Payment + Guest pies */}
                <Row gutter={[14,14]} className="mb-4">
                  <Col xs={24} lg={8}>
                    <Card className="ns-card" title="Conversion Funnel">
                      {(overview.funnel || []).map((s, i, arr) => {
                        const prev = i > 0 ? arr[i-1].value : s.value;
                        return (
                          <div key={i} style={{ padding:"10px 0", borderBottom: i < arr.length-1 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                              <span style={{ fontWeight:700, fontSize:12 }}>{s.step}</span>
                              <span style={{ fontWeight:800, fontSize:12, color:s.color }}>{s.value?.toLocaleString() ?? "N/A"}</span>
                            </div>
                            <Progress percent={prev > 0 ? Math.round((s.value/prev)*100) : 100} strokeColor={s.color} size="small" showInfo={false}/>
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                  <Col xs={24} lg={8}>
                    <MiniPieCard title="Device Breakdown"  data={overview.devicePie  || []}/>
                  </Col>
                  <Col xs={24} lg={8}>
                    <MiniPieCard title="Payment Methods"   data={overview.paymentPie || []}/>
                  </Col>
                </Row>

                {/* Guest type */}
                <Row gutter={[14,14]}>
                  <Col xs={24} lg={8}>
                    <MiniPieCard title="New vs Returning Guests" data={overview.guestPie || []}/>
                  </Col>
                  <Col xs={24} lg={16}>
                    <Card className="ns-card" title="Source Trend (Daily Bookings)">
                      <div style={{ height:220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
                            <XAxis dataKey="date" fontSize={11}/>
                            <YAxis fontSize={11}/>
                            <RTooltip/>
                            <Legend/>
                            <Area type="monotone" dataKey="srcWeb"    name="Web"    stackId="1" fill={COLORS.primary} fillOpacity={0.7} stroke={COLORS.primary}/>
                            <Area type="monotone" dataKey="srcAiBot"  name="AI Bot" stackId="1" fill={COLORS.purple}  fillOpacity={0.7} stroke={COLORS.purple}/>
                            <Area type="monotone" dataKey="srcDirect" name="Direct" stackId="1" fill={COLORS.success} fillOpacity={0.7} stroke={COLORS.success}/>
                            <Area type="monotone" dataKey="srcMeta"   name="Meta"   stackId="1" fill={COLORS.amber}   fillOpacity={0.7} stroke={COLORS.amber}/>
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* ══════════════ AI BOT ══════════════ */}
            {activeTab === "bot" && overview && (
              <>
                {!hasDailyStats && <NaNotice/>}
                <Row gutter={[14,14]} className="mb-4">
                  <Col xs={12} md={6}><KpiCard label="Bot Bookings"      value={fmt(T.botBookings)}      color={COLORS.purple} subLabel="AI Bot Share" subValue={pct(K.aiShare)}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Bot Revenue"        value={T.botRevenue == null ? "N/A" : `₹ ${moneyINR(T.botRevenue)}`} color={COLORS.success}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Conversations"      value={T.botConversations == null ? "N/A" : fmt(T.botConversations)} color={COLORS.cyan}/></Col>
                  <Col xs={12} md={6}><KpiCard label="Msgs/Conversation"  value={T.botConversations ? (T.botMessages/T.botConversations).toFixed(1) : "N/A"} color={COLORS.amber}/></Col>
                </Row>
                <Row gutter={[14,14]}>
                  <Col xs={24} lg={16}>
                    <Card className="ns-card" title="AI Bot Bookings Trend">
                      <div style={{ height:300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
                            <XAxis dataKey="date" fontSize={11}/>
                            <YAxis yAxisId="left" fontSize={11}/>
                            <YAxis yAxisId="right" orientation="right" fontSize={11}/>
                            <RTooltip/>
                            <Legend/>
                            <Bar  yAxisId="left"  dataKey="botConversations" name="Conversations" fill={COLORS.purple}  radius={[4,4,0,0]} opacity={0.7}/>
                            <Line yAxisId="right" type="monotone" dataKey="botBookings" name="Bot Bookings" stroke={COLORS.success} strokeWidth={2} dot={false}/>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} lg={8}>
                    <MiniPieCard title="Bookings by Source" data={overview.sourcePie || []}/>
                  </Col>
                </Row>
              </>
            )}

            {/* ══════════════ CAMPAIGNS ══════════════ */}
            {activeTab === "campaigns" && campaigns.length === 0 && !loading && (
              <Alert type="info" showIcon message="No campaign data found for this period."
                style={{ marginBottom:16, borderRadius:10 }}/>
            )}
            {activeTab === "campaigns" && (
              <Row gutter={[14,14]}>
                <Col xs={24} lg={16}>
                  <Card className="ns-card" title="Campaign Performance">
                    <Table
                      dataSource={campaigns.map((c,i) => ({ ...c, key:i }))}
                      columns={campaignColumns}
                      pagination={{ pageSize:20 }}
                      size="middle"
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  {overview && <MiniPieCard title="Bookings by Source" data={overview.sourcePie || []} extra={<Tag color="purple">All Channels</Tag>}/>}
                </Col>
              </Row>
            )}

            {/* ══════════════ ROOMS ══════════════ */}
            {activeTab === "rooms" && roomTypes.length === 0 && !loading && (
              <Alert type="info" showIcon message="No room booking data found for this period."
                style={{ marginBottom:16, borderRadius:10 }}/>
            )}
            {activeTab === "rooms" && (
              <Row gutter={[14,14]}>
                <Col xs={24}>
                  <Card className="ns-card" title="Room Type Performance">
                    <Table
                      dataSource={roomTypes.map((r,i) => ({ ...r, key:i }))}
                      columns={roomColumns}
                      pagination={false}
                      size="middle"
                    />
                  </Card>
                </Col>
              </Row>
            )}

          </Spin>
        </div>
      </div>

      <style>{`
        .guest-database-container { padding-bottom:18px; }
        .ns-icon-circle { width:40px;height:40px;border-radius:12px;display:grid;place-items:center;background:rgba(22,119,255,0.1);color:#1677ff;box-shadow:0 10px 30px rgba(22,119,255,0.12); }
        .ns-top-control { display:flex;flex-direction:column;gap:4px; }
        .ns-top-label { font-size:11px;color:#6b7280;font-weight:800;text-transform:uppercase;letter-spacing:0.4px; }
        .ns-btn-ghost { height:40px;border-radius:10px;border:1px solid #e5e7eb;background:#fff;font-weight:700; }
        .ns-metric-card { border-radius:14px;box-shadow:0 10px 30px rgba(17,24,39,0.06);border:1px solid rgba(229,231,235,0.9); }
        .ns-metric-top { display:flex;align-items:center;gap:10px;color:#6b7280;font-weight:800;font-size:11px;text-transform:uppercase;letter-spacing:0.4px; }
        .ns-metric-dot { width:8px;height:8px;border-radius:999px;flex-shrink:0; }
        .ns-metric-label { white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
        .ns-metric-value { margin-top:8px;font-size:26px;font-weight:900;letter-spacing:-0.3px;line-height:1.1; }
        .ns-card { border-radius:14px;box-shadow:0 10px 30px rgba(17,24,39,0.06);border:1px solid rgba(229,231,235,0.9); }
        .ns-card .ant-card-head { border-bottom:1px solid rgba(0,0,0,0.06); }
        .ns-card .ant-card-head-title { font-weight:800;font-size:14px; }
        .ant-tabs-tab { font-weight:700 !important;font-size:13px !important;color:#94a3b8 !important; }
        .ant-tabs-tab:hover { color:#1e293b !important; }
        .ant-tabs-tab-active .ant-tabs-tab-btn { color:#1677ff !important;font-weight:800 !important; }
        .ant-tabs-ink-bar { height:3px !important;border-radius:3px !important; }
      `}</style>
    </div>
  );
}